import React, { useMemo, useState } from 'react';
import {
  useWindowDimensions,
  Linking,
  Image,
  View,
  Modal,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import RenderHTML from 'react-native-render-html';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { formattedTextToHtml } from '@/Utils/CommonUtils';
import { MaterialIcons } from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';

// onLinkPress: optional interceptor called before the default external open.
// Return true when it handled the link itself (e.g. an in-app navigate) to
// skip Linking.openURL; return false/undefined (or omit the prop entirely)
// to keep today's behaviour. Existing callers that don't pass it are
// unaffected -- see Screens/Clubs/components/ChatTab.tsx for a consumer.
const CCParsedMessage = ({ text, style, onLinkPress }) => {
  const { width } = useWindowDimensions();
  const fontSize = style?.fontSize ?? FontSizes[14];
  const baseUrl = 'https://crm.circlechess.com';
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Split text into text / image chunks
  const chunks = useMemo(() => {
    const IMAGE_EXTENSIONS = '(png|jpe?g|gif|webp|bmp|svg)';
    const regex = new RegExp(
      // image OR link
      `!\\[([^\\]]*)\\]\\(([^)]+)\\)|\\[([^\\]]+)\\]\\(([^)]+)\\)`,
      'gi'
    );

    let lastIndex = 0;
    const result: {
      type: 'text' | 'image' | 'link';
      content?: string;
      src?: string;
      alt?: string;
      href?: string;
      label?: string;
    }[] = [];

    let match;

    while ((match = regex.exec(text)) !== null) {
      const full = match[0];

      // text before match
      if (match.index > lastIndex) {
        result.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // IMAGE: ![alt](url)
      if (full.startsWith('!')) {
        const alt = match[1];
        const rawUrl = match[2];
        const isImage = new RegExp(`\\.(${IMAGE_EXTENSIONS})(\\?|$)`, 'i').test(rawUrl);

        if (isImage) {
          result.push({
            type: 'image',
            src: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
            alt,
          });
        } else {
          // fallback: treat as link
          result.push({
            type: 'link',
            label: alt || rawUrl,
            href: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
          });
        }
      }
      // LINK: [label](url)
      else {
        const label = match[3];
        const rawUrl = match[4];

        result.push({
          type: 'link',
          label,
          href: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
        });
      }

      lastIndex = match.index + full.length;
    }

    // remaining text
    if (lastIndex < text.length) {
      result.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return result;
  }, [text]);


  return (
    <View>
      {chunks.map((chunk, idx) => {
        if (chunk.type === 'text') {
          const html = formattedTextToHtml(chunk.content ?? '');
          return (
            <RenderHTML
              key={idx}
              contentWidth={width}
              source={{ html }}
              baseStyle={{
                fontSize,
                lineHeight: fontSize * 1.1,
                color: style?.color ?? CCColors?.TextColor?.Primary,
              }}
              defaultTextProps={{ selectable: true }}
              tagsStyles={{
                p: { marginTop: 4, marginBottom: 4, fontSize, lineHeight: fontSize * 1.1 },
                span: { fontSize, lineHeight: fontSize * 1.1 },
                strong: { fontWeight: '700' },
                em: { fontStyle: 'italic' },
                a: { color: CCColors.Level.Blue.Dark, textDecorationLine: 'underline' },
              }}
              renderersProps={{
                a: {
                  onPress: (_, href) => {
                    if (!href) {
                      return;
                    }
                    if (!onLinkPress?.(href)) {
                      Linking.openURL(href);
                    }
                  },
                },
              }}
            />
          );
        }

        if (chunk.type === 'image') {
          return (
            <TouchableOpacity key={idx} onPress={() => setPreviewImage(chunk.src)}>
              <Image
                source={{ uri: chunk.src }}
                style={{ width: 300, height: 200, resizeMode: 'contain', marginVertical: 8 }}
                accessibilityLabel={chunk.alt}
              />
            </TouchableOpacity>
          );
        }
        if (chunk.type === 'link') {
        return (
          <CCText
            key={idx}
            style={{
              color: CCColors.Level.Blue.Dark,
              textDecorationLine: 'underline',
              marginVertical: 4,
            }}
            onPress={() => {
              // onLinkPress handles specific in-app targets (e.g. Caissa
              // tournament links); fall through to a normal external open
              // otherwise.
              if (!onLinkPress?.(chunk.href!)) {
                Linking.openURL(chunk.href!);
              }
            }}>
            {chunk.label}
          </CCText>
        );
      }
      })}

      {/* Fullscreen preview modal */}
      {previewImage && (
        <Modal visible transparent onRequestClose={() => setPreviewImage(null)}>
          <View style={styles.bg}>
            <TouchableOpacity
              onPress={() => setPreviewImage(null)}
              style={styles.closeIcon}>
              <MaterialIcons name="close" size={32} color="white" />
            </TouchableOpacity>

            <Image
              source={{ uri: previewImage }}
              style={{ width: '90%', height: '90%' }}
              resizeMode="contain"
            />
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bg: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeIcon: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 10,
  },
});

export default CCParsedMessage;
